import type { Entity } from "@entities/entitiesClient";
import { useEntityTypeahead } from "@entities/entitiesQueries";
import { faMagnifyingGlass } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { patientsClient } from "@patients/patientsClient";
import { cn } from "@scribenote/brian-design-system";
import { useOpenPatient } from "@shell/navigation";
import { useQuery } from "@tanstack/react-query";
import { useId, useImperativeHandle, useRef, useState, type Ref } from "react";

export interface SearchFieldHandle {
  focus: () => void;
}

interface Row {
  id: string;
  name: string;
  detail: string | null;
  client: Entity | null;
}

export const SearchField = ({ ref }: { ref?: Ref<SearchFieldHandle> }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const openPatient = useOpenPatient();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [client, setClient] = useState<Entity | null>(null);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }));

  const { data, ready, isFetching } = useEntityTypeahead(query, [
    "patient",
    "client",
  ]);
  const clientPatients = useQuery({
    queryKey: ["client-patients", client?.id],
    queryFn: () => patientsClient.listForClient(client!.id),
    enabled: client !== null,
  });

  const rows: Row[] = client
    ? (clientPatients.data ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        detail: client.name,
        client: null,
      }))
    : (data ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        detail:
          s.kind === "client"
            ? "Client"
            : s.kind === "household"
              ? "Household"
              : (s.clientName ?? null),
        client: s.kind === "client" ? s : null,
      }));

  const reset = () => {
    setQuery("");
    setClient(null);
    setActive(0);
    setOpen(false);
  };

  const choose = (row: Row) => {
    if (row.client) {
      setClient(row.client);
      setActive(0);
      return;
    }
    openPatient(row.id);
    reset();
    inputRef.current?.blur();
  };

  const showList = open && (ready || client !== null);
  const pending = client ? clientPatients.isPending : isFetching || !ready;

  return (
    <div className="relative w-full max-w-md">
      <label className="flex h-10 items-center gap-2.5 rounded-full border border-rule bg-card px-4 focus-within:border-accent">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          aria-hidden="true"
          className="text-sm text-annotation"
        />
        <input
          ref={inputRef}
          type="search"
          value={query}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={
            showList && rows[active] ? `${listId}-${active}` : undefined
          }
          aria-label="Search patients, clients, records"
          placeholder="Search patients, clients, records"
          data-testid="header-search-field"
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            setQuery(e.target.value);
            setClient(null);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              reset();
              inputRef.current?.blur();
            } else if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, rows.length - 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter" && rows[active]) {
              e.preventDefault();
              choose(rows[active]);
            }
          }}
          className="min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-placeholder [&::-webkit-search-cancel-button]:hidden"
        />
      </label>
      {showList && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Search results"
          className="absolute inset-x-0 top-12 z-dropdown max-h-[50vh] overflow-y-auto rounded-sheet border border-rule bg-card p-1.5 shadow-float"
        >
          {rows.length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-annotation">
              {pending ? "Looking." : "Nothing by that name."}
            </li>
          ) : (
            rows.map((row, i) => (
              <li
                key={row.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(row)}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  "flex cursor-pointer items-baseline justify-between gap-3 rounded-tag px-3 py-2 text-sm",
                  i === active && "bg-recess",
                )}
              >
                <span className="truncate font-medium text-ink">
                  {row.name}
                </span>
                {row.detail && (
                  <span className="shrink-0 truncate text-annotation">
                    {row.detail}
                  </span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
